import {Component, OnDestroy, OnInit} from '@angular/core';
import {Page, Pageable} from "../../utils/pageable";
import {catchError, forkJoin, interval, map, merge, Observable, of, Subscription, switchMap, tap, zip} from "rxjs";
import {AuthorizationServiceProvider} from "../../auth_profile/services/authorization-service.provider";
import {SpinnerService} from "../../utils/services/spinner.service";
import {environment} from 'src/environments/environment';
import {WebSocketService} from 'src/app/col/services/web-socket.service';
import * as LessonsV3 from '../../col/model/v3/lessons';
import {NativeServiceApiProvider} from "../../services/native-api-provider.service";

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  // The card shows the single nearest lesson, so only lessons still ahead of the student
  // qualify - a cancelled or already closed one must not take that slot.
  private readonly nextLessonStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Booked,
    LessonsV3.LessonStatus.Due,
    LessonsV3.LessonStatus.InProgress,
  ];

  lessons: Page<LessonsV3.LFStudentGeneral>;
  studentId: number
  schoolId: number
  personId: number
  starterOffers = [
    { lang: 'en', cssClass: 'course-en', countdown: '', target: undefined as Date | undefined },
    { lang: 'sp', cssClass: 'course-sp', countdown: '', target: undefined as Date | undefined }
  ]

  introductionState: string | null = null

  wsSubscription: Subscription
  countdownSubscription: Subscription
  dashboardSubscription: Subscription
  provaContexts: LessonsV3.ColProvaContextDetails[]
  provaContextLoaded = false

  constructor(private authService: AuthorizationServiceProvider,
              private spinner: SpinnerService,
              private provider: NativeServiceApiProvider,
              private webSocket: WebSocketService
  ) { }

  ngOnInit(): void {
    this.countdownSubscription = interval(1000).subscribe(() => this.updateStarterCountdown())
    this.dashboardSubscription = this.authService.getAuthDetailsService().pipe(
        switchMap(api => zip(api.getSelfSchoolId(), api.getSelfStudentId(), api.getSelfPersonId())),
        tap(([schoolId, studentId, personId]) => {
          this.schoolId = schoolId
          this.studentId = studentId
          this.personId = personId
        }),
        switchMap(() =>
          forkJoin([
            this.getLessons(),
            this.loadProvaContext(),
            this.loadTechnicalProfile()
          ])
        ),
        tap(() => this.startWebSocketListener()),
        catchError(() => of(null))
    ).subscribe()
  }

  ngOnDestroy(): void {
    this.dashboardSubscription?.unsubscribe()
    this.wsSubscription?.unsubscribe()
    this.countdownSubscription?.unsubscribe()
  }

  getLessons(): Observable<Page<LessonsV3.LFStudentGeneral>> {
    if (!this.studentId)
      return of(null);
    // One hour back, as the legacy next-lesson endpoint did, so a lesson already under way
    // keeps its place on the card instead of disappearing minutes after it starts.
    const dateFrom = new Date(Date.now() - 60 * 60 * 1000);
    return this.spinner.trace<Page<LessonsV3.LFStudentGeneral>>(
        this.provider.colV3().pipe(
            switchMap((api) =>
                api.findLessons(
                    LessonsV3.LessonCategory.Col,
                    null,
                    'student-full',
                    null,
                    this.studentId,
                    null,
                    null,
                    null,
                    null,
                    this.nextLessonStatuses,
                    dateFrom,
                    null,
                    new Pageable(0, 1, ['metric.details.plannedStartDate,ASC'])
                )
            ),
            map((lessons) => this.asStudentGeneralPage(lessons)),
            tap((lessons) => (this.lessons = lessons)),
        )
    );
  }

  private asStudentGeneralPage(
    page: Page<LessonsV3.LessonView>
  ): Page<LessonsV3.LFStudentGeneral> {
    if (!page) {
      return null;
    }
    return {
      ...page,
      content: (page.content ?? []) as LessonsV3.LFStudentGeneral[],
    };
  }

  loadTechnicalProfile(): Observable<LessonsV3.PersonalSettings | null> {
    if (!this.personId) {
      return of(null);
    }
    return this.provider.colV3().pipe(
      switchMap((api) => api.getPersonColSettings(this.personId)),
      tap((settings) => {
        this.introductionState = settings?.introductionState ?? null
      }),
      catchError(() => of(null))
    )
  }

  private startWebSocketListener(): void {
    if (!this.studentId || this.wsSubscription) return
    this.wsSubscription = this.webSocket.establish(environment.eventsWsEndpoint)
      .pipe(
        switchMap((connection) =>
          merge(
            connection.subscribe(`/col/student/${this.studentId}/LessonListUpdate`),
            connection.subscribe(`/col/student/${this.studentId}/LessonUpdate`)
          )),
        switchMap(() => this.getLessons())
      ).subscribe()
  }

  private updateStarterCountdown() {
    this.starterOffers.forEach(offer => {
      if (this.isValidDate(offer.target)) {
        offer.countdown = this.formatCountdown(offer.target)
      }
    })
  }

  private formatCountdown(target: Date): string {
    const now = new Date().getTime()
    const diff = Math.max(target.getTime() - now, 0)
    const totalSeconds = Math.floor(diff / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${days} days ${this.pad2(hours)}:${this.pad2(minutes)}:${this.pad2(seconds)}`
  }

  private pad2(value: number): string {
    return String(value).padStart(2, '0')
  }

  loadProvaContext() {
    if (!this.studentId || !this.schoolId) return of(null);
    this.provaContextLoaded = false
    return this.spinner
        .trace<LessonsV3.ColProvaContextDetails[]>(
            this.provider.colV3().pipe(
                switchMap((api) =>
                    forkJoin(
                        this.starterOffers.map((offer) =>
                            api.getProvaContext(this.schoolId, this.studentId, offer.lang).pipe(
                                map((context) => ({lang: offer.lang, context}))
                            )
                        )
                    )
                ),
          tap((results) => this.applyProvaContexts(results)),
                map((results) =>
                    results
                        .map((result) => result.context)
                        .filter((context): context is LessonsV3.ColProvaContextDetails => !!context)
                ),
          tap((contexts) => this.provaContexts = contexts),
          tap(_ => this.provaContextLoaded = true)
        )
      )
  }

  private applyProvaContexts(results: { lang: string, context: LessonsV3.ColProvaContextDetails | null }[]) {
    this.starterOffers.forEach(offer => {
      const result = results.find(r => r.lang === offer.lang)
      const expiry = result?.context?.startExpiryDate
      offer.target = expiry ? new Date(expiry) : undefined
    })
  }

  isStarterAvailable(offer: { target?: Date }) {
    if (!this.provaContextLoaded) return false
    const now = new Date().getTime()
    return this.isValidDate(offer.target) && offer.target.getTime() > now
  }

  private isValidDate(value?: Date): value is Date {
    return !!value && !isNaN(value.getTime())
  }
}
